import React from "react";
import { Box, Divider } from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
    DotLegalCompletedPercentage,
    DotLegalFixedHeightText,
    DotLegalHeader,
    DotLegalHeading,
    DotLegalPaper,
    GridSizes,
} from "@dotlegal/dotlegal-ui-components";
import DotLegalLink from "@common/components/dotLegalLink/DotLegalLink";
import QuestionnaireRisk from "@common/components/questionnaireComponents/questionnaireRisk/QuestionnaireRisk";
import { getQuestionnaireResponseScore } from "@common/questionnaireResponseScore";
import { useTranslation } from "@localization/useTranslation";
import { useUserContext } from "../../../../auth/userContextProvider/UserContextProvider";
import { useUrlProvider } from "../../../../useUrlProvider";
import { LegalEntityAuditStatus } from "../../../../legalEntity/legalEntityAuditTab/LegalEntityAudit.types";
import { RiskLevel } from "../../../../legalEntity/legalEntityClassificationTab/LegalEntityClassificationTab.types";
import QuestionnaireTemplateLink from "../../../../legalEntity/legalEntityAuditTab/QuestionnaireTemplateLink";

export interface ManagementReportSummaryProps {
    isPdf: boolean;
    sent: Date | undefined;
    deadline: Date;
    progress: number;
    templateId: string;
    templateName: string;
    // Audit groups and internal assessment groups name their questionnaire and their recipients differently, so the
    // labels travel with the data instead of being branched on inside the card.
    templateLabelKey: string;
    respondentLabelKey: string;
    respondentCount: number;
    openedCount: number;
    submittedCount: number;
    excludedCount: number;
    responsible: string;
    sender: string | undefined;
    senderId: string | undefined;
    scoreEnabled: boolean;
    overallRiskLevel: RiskLevel | undefined;
    overallRiskLevelName: string | undefined;
    overallRiskScore: number | undefined;
}

const dividerSx = {
    backgroundColor: "#F0F7FC",
    marginBottom: 2,
    marginTop: 2,
    marginLeft: 6,
    marginRight: 6,
};

function ManagementReportSummary(props: ManagementReportSummaryProps) {
    const { translateString, translateLegalEntityAuditStatus, translateDate } = useTranslation();
    const { customerName } = useUserContext();
    const { getGroupEntityUrl } = useUrlProvider();

    const gridSizes: GridSizes = {
        xs: 12,
        sm: props.isPdf ? 3 : 6,
        md: "grow",
    };

    function getStatus() {
        if (props.progress === 100) {
            return translateString("completed");
        }

        return props.sent ? translateLegalEntityAuditStatus(LegalEntityAuditStatus.InProgress.toString()) : "";
    }

    function getSender() {
        if (!props.sender) {
            return customerName;
        }

        if (!props.senderId) {
            return props.sender;
        }

        return (
            <DotLegalLink bold linkColor={"primary"} to={getGroupEntityUrl(props.senderId)}>
                {props.sender}
            </DotLegalLink>
        );
    }

    function getOverallRiskLevelAndScore() {
        if (!props.overallRiskLevelName || !props.overallRiskScore) {
            return undefined;
        }

        return (
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1, fontWeight: "bold", marginTop: "6px" }}>
                <QuestionnaireRisk
                    riskLevel={props.overallRiskLevel}
                    riskName={props.overallRiskLevelName}
                    scoreEnabled={props.scoreEnabled}
                    status={LegalEntityAuditStatus.Submitted}
                />
                ({getQuestionnaireResponseScore(true, props.scoreEnabled, props.overallRiskScore, translateString("scoreNotEnabled"))})
            </Box>
        );
    }

    return (
        <Box mt={2}>
            <DotLegalHeader headerStyle={"xxxl"}>{translateString("summary")}</DotLegalHeader>
            <DotLegalPaper
                sx={{ marginBottom: 2, minHeight: 132, backgroundPositionX: "right", boxShadow: "none" }}
                background="purpleWave2"
                backgroundColor={"lightPurple"}
            >
                <Grid container>
                    <Grid size={gridSizes}>
                        <DotLegalHeading name={translateString("status")}>
                            <DotLegalFixedHeightText fontSize="xs" content={getStatus()} />
                        </DotLegalHeading>
                    </Grid>
                    <Grid size={gridSizes}>
                        <DotLegalHeading name={translateString("sent")}>
                            <DotLegalFixedHeightText fontSize="xs" content={props.sent ? translateDate(props.sent) : ""} />
                        </DotLegalHeading>
                    </Grid>
                    <Grid size={gridSizes}>
                        <DotLegalHeading name={translateString(props.templateLabelKey)}>
                            <DotLegalFixedHeightText
                                fontSize="xs"
                                content={<QuestionnaireTemplateLink id={props.templateId} name={props.templateName} type={"audit"} />}
                            />
                        </DotLegalHeading>
                    </Grid>
                    <Grid size={gridSizes}>
                        <DotLegalHeading name={translateString("deadline")}>
                            <DotLegalFixedHeightText fontSize="xs" content={translateDate(props.deadline)} />
                        </DotLegalHeading>
                    </Grid>
                </Grid>

                <Divider sx={dividerSx} />

                <Grid container>
                    <Grid size={gridSizes}>
                        <DotLegalHeading name={translateString(props.respondentLabelKey)}>
                            <DotLegalFixedHeightText content={props.respondentCount} fontSize="xl" />
                        </DotLegalHeading>
                    </Grid>
                    <Grid size={gridSizes}>
                        <DotLegalHeading name={translateString("opened")}>
                            <DotLegalFixedHeightText content={props.openedCount} fontSize="xl" />
                        </DotLegalHeading>
                    </Grid>
                    <Grid size={gridSizes}>
                        <DotLegalHeading name={translateString("submitted")}>
                            <DotLegalFixedHeightText content={props.submittedCount} fontSize="xl" />
                        </DotLegalHeading>
                    </Grid>
                    <Grid size={gridSizes}>
                        <DotLegalHeading name={translateString("excluded")}>
                            <DotLegalFixedHeightText content={props.excludedCount} fontSize="xl" />
                        </DotLegalHeading>
                    </Grid>
                </Grid>

                <Divider sx={dividerSx} />

                <Grid container>
                    <Grid size={gridSizes}>
                        <DotLegalHeading name={translateString("responsible")}>
                            <DotLegalFixedHeightText fontSize="xs" content={props.responsible} />
                        </DotLegalHeading>
                    </Grid>
                    <Grid size={gridSizes}>
                        <DotLegalHeading name={translateString("sentFrom")}>
                            <DotLegalFixedHeightText fontSize="xs" content={getSender()} />
                        </DotLegalHeading>
                    </Grid>
                    {props.scoreEnabled && (
                        <Grid size={gridSizes}>
                            <DotLegalHeading name={translateString("overallRiskLevelAndScore")}>
                                <DotLegalFixedHeightText fontSize="xs" content={getOverallRiskLevelAndScore()} />
                            </DotLegalHeading>
                        </Grid>
                    )}
                    <Grid size={gridSizes}>
                        <DotLegalHeading name={translateString("progress")}>
                            <DotLegalFixedHeightText
                                fontSize="sm"
                                content={
                                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                                        <Box width={238}>
                                            <DotLegalCompletedPercentage completed={props.progress} />
                                        </Box>
                                    </Box>
                                }
                            />
                        </DotLegalHeading>
                    </Grid>
                    {!props.scoreEnabled && <Grid size={gridSizes}></Grid>}
                </Grid>
            </DotLegalPaper>
        </Box>
    );
}

export default ManagementReportSummary;
